import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { googleLogin, emailSignup, clearError } from "../../redux/slices/authSlice";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ArrowBackIosOutlined from "@mui/icons-material/ArrowBackIosOutlined";
import config from "../../config/config";

const UserSignup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, isAuthenticated, isNewUser, loadingProvider, pendingVerificationEmail } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    gender: "",
    country: "",
    city: "",
  });

  const [validationError, setValidationError] = useState("");

  const googleButtonRef = useRef(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEmailSignup = (e) => {
    e.preventDefault();
    setValidationError("");

    if (formData.password !== formData.confirmPassword) {
      return setValidationError("Passwords do not match");
    }
    if (formData.password.length < 8) {
      return setValidationError("Password must be at least 8 characters");
    }

    dispatch(emailSignup({
      name: formData.name,
      email: formData.email,
      password: formData.password,
      gender: formData.gender,
      country: formData.country,
      city: formData.city,
    }));
  };

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (pendingVerificationEmail) {
      navigate("/verify-email", { state: { email: pendingVerificationEmail, from: location.state?.from } });
    } else if (isAuthenticated) {
      const from = location.state?.from;
      if (isNewUser) {
        navigate("/profile", { state: { from: from || "/" } });
      } else {
        if (from) {
          navigate(from, { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      }
    }
  }, [pendingVerificationEmail, isAuthenticated, isNewUser, navigate, location.state]);

  useEffect(() => {
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id: config.googleClientId,
        callback: (response) => dispatch(googleLogin(response.credential)),
      });
      window.google.accounts.id.renderButton(
        googleButtonRef.current,
        { theme: "outline", size: "large", width: "100%", shape: "pill" }
      );
    }
  }, [dispatch]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(ellipse at 15% 10%, rgba(255, 155, 128, 0.12) 0%, transparent 45%), radial-gradient(ellipse at 85% 85%, rgba(255, 140, 56, 0.08) 0%, transparent 45%), #FAFAFA",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top Header */}
      <Box
        sx={{
          py: { xs: 2, sm: 2.5 },
          px: { xs: 2.5, sm: 4 },
          width: "100%",
        }}
      >
        <img
          src={require("../../assets/images/handpik.png")}
          alt="Handpik"
          width={150}
          height={30}
          style={{ cursor: "pointer", display: "block" }}
          onClick={() => navigate("/")}
        />
      </Box>

      {/* Main Center Area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: { xs: "flex-start", sm: "flex-start" },
          justifyContent: "center",
          px: { xs: 2, sm: 3 },
          pb: { xs: 4, sm: 6 },
          pt: { xs: 1, sm: 3 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: { xs: "100%", sm: 480 }, position: "relative" }}>
          <IconButton
            onClick={handleBack}
            aria-label="Back"
            sx={{
              position: { xs: "static", sm: "absolute" },
              left: { sm: -56 },
              top: { sm: 6 },
              mb: { xs: 2, sm: 0 },
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(6px)",
              border: "1px solid rgba(0, 0, 0, 0.08)",
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
              width: { xs: 42, sm: 40 },
              height: { xs: 42, sm: 40 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                backgroundColor: "#fff",
                transform: "scale(1.05)",
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
              },
              transition: "all 0.2s ease",
            }}
          >
            <ArrowBackIosOutlined sx={{ fontSize: { xs: "18px", sm: "17px" }, color: "#1a1a1a", ml: "4px" }} />
          </IconButton>

          <Paper
            elevation={0}
            sx={{
              width: "100%",
              borderRadius: { xs: "24px", sm: "28px" },
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(0, 0, 0, 0.06)",
              boxShadow: {
                xs: "0 10px 35px -5px rgba(0, 0, 0, 0.07), 0 0 1px rgba(0,0,0,0.08)",
                sm: "0 20px 50px -10px rgba(0, 0, 0, 0.05), 0 0 1px rgba(0,0,0,0.08)",
              },
              overflow: "hidden",
            }}
          >
            {/* Top Brand Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                pt: { xs: 4.5, sm: 5 },
                pb: { xs: 3, sm: 3.5 },
                px: { xs: 3, sm: 5 },
                background:
                  "linear-gradient(180deg, rgba(255, 155, 128, 0.06) 0%, transparent 100%)",
              }}
            >
              <Box
                sx={{
                  width: { xs: 70, sm: 68 },
                  height: { xs: 70, sm: 68 },
                  borderRadius: "22px",
                  bgcolor: "rgba(255, 140, 56, 0.08)",
                  border: "1px solid rgba(255, 140, 56, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                  boxShadow: "0 8px 24px rgba(255, 140, 56, 0.10)",
                }}
              >
                <img
                  src={require("../../assets/images/login-handpik-logo_cropped.png")}
                  alt="Handpik"
                  style={{ width: 40, height: 40, objectFit: "contain" }}
                />
              </Box>

              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  bgcolor: "rgba(255, 140, 56, 0.1)",
                  color: "#FF8C38",
                  borderRadius: "100px",
                  px: 2.2,
                  py: 0.7,
                  fontSize: { xs: "12px", sm: "11px" },
                  fontWeight: 700,
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                }}
              >
                Join Handpik
              </Box>
            </Box>

            <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.05)" }} />

            {/* Sign-up Action Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                px: { xs: 3, sm: 5 },
                pt: { xs: 3.5, sm: 4 },
                pb: { xs: 4.5, sm: 5 },
              }}
            >
              <Typography
                component="h1"
                sx={{
                  fontFamily: "'Lora', serif",
                  fontSize: { xs: "25px", sm: "28px" },
                  fontWeight: 600,
                  color: "#1A1A1A",
                  letterSpacing: "-0.02em",
                  textAlign: "center",
                  mb: 1.2,
                }}
              >
                Create an Account
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#71717A",
                  fontSize: { xs: "14px", sm: "13.5px" },
                  textAlign: "center",
                  lineHeight: 1.55,
                  maxWidth: { xs: "100%", sm: "340px" },
                  mb: 2,
                }}
              >
                Sign up to start your personalized fashion experience.
              </Typography>

              {(error || validationError) && (
                <Alert
                  severity="error"
                  sx={{
                    width: "100%",
                    maxWidth: "360px",
                    mb: 2.5,
                    borderRadius: "12px",
                    fontSize: "13px",
                  }}
                >
                  {validationError || error}
                </Alert>
              )}

              {/* Action Container (Google Button & Loading) */}
              <Box
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", sm: "360px" },
                  minHeight: "44px",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                }}
              >
                <Box
                  ref={googleButtonRef}
                  sx={{
                    width: "100%",
                    display: loading && loadingProvider === 'google' ? "none" : "flex",
                    justifyContent: "center",
                    "& > div": {
                      width: "100% !important",
                      display: "flex !important",
                      justifyContent: "center !important",
                    },
                  }}
                />
                {loading && loadingProvider === 'google' && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 1,
                    }}
                  >
                    <CircularProgress size={32} sx={{ color: "#FF8C38" }} />
                  </Box>
                )}
              </Box>

              <Divider sx={{ width: "100%", maxWidth: "360px", mb: 3, color: "#A1A1AA", fontSize: "12px" }}>
                OR
              </Divider>

              {/* Email Signup Form */}
              <Box
                component="form"
                onSubmit={handleEmailSignup}
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", sm: "360px" },
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  mb: 1,
                }}
              >
                <TextField
                  label="Full Name"
                  name="name"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleChange}
                  disabled={loading && loadingProvider === 'email'}
                />
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading && loadingProvider === 'email'}
                />
                <TextField
                  label="Password"
                  name="password"
                  type="password"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading && loadingProvider === 'email'}
                />
                <TextField
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={loading && loadingProvider === 'email'}
                />

                <FormControl fullWidth variant="outlined">
                  <InputLabel id="gender-label">Gender (Optional)</InputLabel>
                  <Select
                    labelId="gender-label"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    label="Gender (Optional)"
                    disabled={loading && loadingProvider === 'email'}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    <MenuItem value="Men">Men</MenuItem>
                    <MenuItem value="Women">Women</MenuItem>
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Country (Optional)"
                    name="country"
                    variant="outlined"
                    fullWidth
                    value={formData.country}
                    onChange={handleChange}
                    disabled={loading && loadingProvider === 'email'}
                  />
                  <TextField
                    label="City (Optional)"
                    name="city"
                    variant="outlined"
                    fullWidth
                    value={formData.city}
                    onChange={handleChange}
                    disabled={loading && loadingProvider === 'email'}
                  />
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading && loadingProvider === 'email'}
                  sx={{
                    py: 1.5,
                    bgcolor: "#FF8C38",
                    color: "white",
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "15px",
                    borderRadius: "12px",
                    boxShadow: "0 4px 14px rgba(255, 140, 56, 0.25)",
                    "&:hover": {
                      bgcolor: "#e67a2a",
                    },
                  }}
                >
                  {loading && loadingProvider === 'email' ? <CircularProgress size={24} color="inherit" /> : "Sign Up"}
                </Button>
              </Box>

              {/* Login Link */}
              <Typography
                variant="body2"
                sx={{
                  color: "#71717A",
                  mt: 3,
                  mb: 1,
                  textAlign: "center",
                }}
              >
                Already have an account?{" "}
                <Link
                  to="/login"
                  style={{
                    color: "#FF8C38",
                    textDecoration: "none",
                    fontWeight: 600,
                  }}
                >
                  Log in
                </Link>
              </Typography>

              {/* Terms & Privacy info */}
              <Typography
                variant="caption"
                sx={{
                  color: "#A1A1AA",
                  fontSize: "11.5px",
                  textAlign: "center",
                  mt: 2,
                  lineHeight: 1.6,
                  maxWidth: "320px",
                }}
              >
                By continuing, you agree to Handpik's{" "}
                <Link
                  to="/terms-of-use"
                  style={{
                    color: "#71717A",
                    textDecoration: "underline",
                    fontWeight: 500,
                  }}
                >
                  Terms of Use
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy-policy"
                  style={{
                    color: "#71717A",
                    textDecoration: "underline",
                    fontWeight: 500,
                  }}
                >
                  Privacy Policy
                </Link>
                .
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default UserSignup;
